/**
 * Os valores normativos do "Guia de marca e sistema visual — Inside Sistemas"
 * (V1, 2026), transcritos uma única vez.
 *
 * Nenhum componente lê deste arquivo: quem consome é `theme.ts`, que mapeia
 * estes valores para os tokens da interface. A separação existe porque as duas
 * coisas mudam por motivos diferentes — a marca muda quando o guia é revisado,
 * o tema muda quando a interface precisa de um token novo.
 */

/** Roxo #5B2A8C é a cor primária e dominante. */
export const ROXO = {
  50: '#F4EFF9',
  200: '#CBB2E2',
  400: '#855BB2',
  500: '#5B2A8C',
  700: '#3D1C60',
} as const;

/** Amarelo #F9AE00 é a secundária, usada como acento — nunca como fundo de grandes áreas. */
export const AMARELO = {
  50: '#FFF8E6',
  200: '#FFDD85',
  300: '#FCC73F',
  400: '#F9AE00',
  600: '#B87E00',
} as const;

export const NEUTROS = {
  ink800: '#2B1D40',
  gray700: '#3F3B52',
  gray500: '#757184',
  gray200: '#E2E1E7',
  gray50: '#F7F7F7',
  branco: '#FFFFFF',
} as const;

/** O único gradiente permitido pelo guia. */
export const GRADIENTE_DE_MARCA =
  'linear-gradient(155deg, #2B1D40, #5B2A8C 55%, #7C46AD)';

/** Tom intermediário do gradiente, o único roxo fora da rampa que o guia cita. */
export const ROXO_GRADIENTE = '#7C46AD';

/**
 * Product Sans, em dois pesos (400 e 700). Os arquivos são proprietários e não
 * moram no repositório: o `@font-face` está em `public/fonts/product-sans.css`,
 * e até os arquivos chegarem a pilha cai para o substituto que o próprio guia
 * define — Google Sans, depois Segoe UI ou a sans do sistema.
 */
export const FAMILIA_DE_MARCA =
  `'Product Sans', 'Google Sans', 'Segoe UI', system-ui, -apple-system, sans-serif`;

/**
 * A escala tipográfica do guia, em px, com a entrelinha de cada degrau.
 *
 * `dado` não vem do guia: é a exceção acordada para número em grade densa
 * (célula de tabela, célula de pivot, rótulo de eixo), onde o corpo de 16 px
 * faria a tabela deixar de caber na tela. Fora dessa exceção, texto é `corpo`
 * ou maior — o guia é explícito nisso.
 */
export const TIPOGRAFIA = {
  hero: { tamanho: '88px', entrelinha: 1.0 },
  h1: { tamanho: '48px', entrelinha: 1.12 },
  h2: { tamanho: '36px', entrelinha: 1.12 },
  h3: { tamanho: '28px', entrelinha: 1.28 },
  h4: { tamanho: '22px', entrelinha: 1.28 },
  corpo: { tamanho: '16px', entrelinha: 1.65 },
  apoio: { tamanho: '14px', entrelinha: 1.5 },
  micro: { tamanho: '12px', entrelinha: 1.5 },
  dado: { tamanho: '13px', entrelinha: 1.45 },
} as const;

/** Títulos em bold com tracking negativo; labels em caixa-alta com tracking aberto. */
export const TRACKING = {
  titulo: '-0.01em',
  eyebrow: '0.14em',
} as const;

/** Grade base de 4 px. */
export const ESPACAMENTO = ['4px', '8px', '12px', '16px', '24px', '32px', '48px', '64px', '80px'];

export const RAIOS = ['4px', '8px', '12px', '16px', '24px'];

/** Sombras sempre tingidas com o ink da marca — nunca preto puro. */
export const SOMBRAS = {
  sm: '0 1px 2px rgba(43,29,64,.06), 0 1px 3px rgba(43,29,64,.08)',
  md: '0 2px 4px rgba(43,29,64,.06), 0 8px 24px rgba(43,29,64,.08)',
  lg: '0 4px 8px rgba(43,29,64,.08), 0 16px 40px rgba(43,29,64,.12)',
  brand: '0 8px 32px rgba(91,42,140,.28)',
} as const;

/** As mesmas sombras no tema escuro, onde o ink já é o fundo e precisa de preto. */
export const SOMBRAS_ESCURAS = {
  sm: '0 1px 2px rgba(10,5,20,.5), 0 1px 3px rgba(10,5,20,.4)',
  md: '0 2px 4px rgba(10,5,20,.5), 0 8px 24px rgba(10,5,20,.45)',
  lg: '0 4px 8px rgba(10,5,20,.55), 0 16px 40px rgba(10,5,20,.5)',
  brand: '0 8px 32px rgba(124,70,173,.45)',
} as const;

export const MOVIMENTO = {
  rapido: '120ms',
  base: '200ms',
  lento: '340ms',
  easing: 'cubic-bezier(.22,1,.36,1)',
} as const;
